import React from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { Box } from '@material-ui/core';

import { fullName } from '../../../../Utils/Format/Names';
import { formatAddress } from '../../../../Utils/Format/Address';
import EmailIcon from '../../../../Components/CustomIcons/EmailIcon';
import TrashIcon from '../../../../Components/CustomIcons/IcTrash';
import PencilIcon from '../../../../Components/CustomIcons/IcPencil';
import PointsIcon from '../../../../Components/CustomIcons/IcPointsFireBadge';
import DocumentIcon from '../../../../Components/CustomIcons/DocumentIcon';
import MobileIcon from '../../../../Components/CustomIcons/MobileIcon';

import SimpleDivider from '../../../../Components/Dividers/SimpleDivider';
import { getSendingMethodText } from '../../../../Enums/invoices';
import { Company } from '../../../../Configs/general';
import ControlledSwitchButton from '../../../../Components/Controlled/ControlledSwitchButton';

const ContractItemGDC = props => {
  const {
    contract,
    displayPoints = false,
    removeContract,
    openEditDialog
  } = props;

  const {
    digitalInvoice,
    sendEmailInvoice,
    sendWhatsAppInvoice,
    sendSmsInvoice
  } = contract;

  const classes = useStyles();

  const handleEditDialog = React.useCallback(() => {
    openEditDialog(contract);
  }, [contract, openEditDialog]);

  const handleRemoveContract = React.useCallback(() => {
    removeContract(contract);
  }, [contract, removeContract]);

  const sendingInvoiceMethod = getSendingMethodText(
    sendEmailInvoice,
    sendWhatsAppInvoice,
    sendSmsInvoice
  );

  return (
    <>
      <Grid container direction="row" className={classes.contractItem}>
        <Grid item xs={3} className={classes.textContainer}>
          <Typography className={classes.label}>
            {Company.contractConjugation.capitalized.singular.associate}
          </Typography>
          <Typography className={classes.text}>
            {`${contract.alias} - ${contract.id}`}
          </Typography>
          {displayPoints && (
            <Grid className={classes.pointsContainer}>
              <PointsIcon className={classes.noShrink} />
              <Typography className={classes.pointsText}>
                {contract.points} puntos
              </Typography>
            </Grid>
          )}
        </Grid>

        <Grid item xs={7} className={classes.textContainer}>
          <Typography className={classes.label}>Nombre del titular</Typography>
          <Typography className={`${classes.text} ${classes.capitalizeText}`}>
            {fullName(contract.holder, 'name', 'lastName', true)}
          </Typography>
        </Grid>

        <Grid item xs={2} className={classes.actionsContainer}>
          <IconButton
            aria-label={`editar ${Company.contractConjugation.regular.singular.main}`}
            onClick={handleEditDialog}
            className={classes.icon}
          >
            <PencilIcon size={20} />
          </IconButton>
          <IconButton
            aria-label={`desasociar ${Company.contractConjugation.regular.singular.main}`}
            onClick={handleRemoveContract}
            className={classes.icon}
          >
            <TrashIcon size={20} />
          </IconButton>
        </Grid>

        <Grid item xs={12} className={classes.addressContainer}>
          <div>
            <Typography className={clsx(classes.label, classes.labelMargin)}>
              Dirección del predio
            </Typography>
            <Typography className={`${classes.text} ${classes.capitalizeText}`}>
              {formatAddress(contract, true)}
            </Typography>
          </div>
        </Grid>

        <Grid item xs={12} className={classes.addressContainer}>
          {sendingInvoiceMethod ? (
            <Grid
              container
              className={classes.activeChannelsContainer}
              wrap="nowrap"
            >
              <EmailIcon size={16} className={classes.noShrink} />
              <Typography className={classes.activeChannelsText}>
                {`Envío de factura vía ${sendingInvoiceMethod} mensual programado.`}
              </Typography>
            </Grid>
          ) : (
            <Grid
              container
              className={classes.activeChannelsContainer}
              wrap="nowrap"
            />
          )}
          <Box className={classes.digitalInvoiceContainer}>
            <Typography
              className={clsx(classes.label, classes.digitalInvoiceButton)}
            >
              Medio de envío de la factura
            </Typography>
            <ControlledSwitchButton
              checked={digitalInvoice}
              onChange={handleEditDialog}
              leftText="Física"
              leftIcon={<DocumentIcon size={20} />}
              rightIcon={<MobileIcon size={18} />}
              rightText="Digital"
            />
          </Box>
        </Grid>
      </Grid>

      <SimpleDivider withoutMargin={true} className={classes.dividerLast} />
    </>
  );
};

const useStyles = makeStyles(theme => ({
  contractItem: {
    padding: theme.spacing(3)
  },
  actionsContainer: {
    textAlign: 'end'
  },
  textContainer: {
    paddingLeft: theme.spacing(),
    paddingRight: theme.spacing()
  },
  addressContainer: {
    paddingLeft: theme.spacing(),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  pointsContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 15
  },
  pointsText: {
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: theme.spacing()
  },
  label: {
    fontSize: 11,
    fontWeight: 500,
    color: theme.palette.text.greyDark
  },
  labelMargin: {
    marginTop: 15
  },
  digitalInvoiceButton: {
    textAlign: 'right',
    marginRight: theme.spacing()
  },
  text: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.text.default
  },
  capitalizeText: {
    textTransform: 'capitalize'
  },
  activeChannelsContainer: {
    alignItems: 'center',
    marginTop: 12
  },
  activeChannelsText: {
    fontSize: 12,
    color: theme.palette.text.greyDark,
    marginLeft: theme.spacing()
  },
  icon: {
    padding: theme.spacing(),
    marginRight: theme.spacing()
  },
  noShrink: {
    flexShrink: 0
  },
  dividerLast: {
    '&:last-child': {
      display: 'none'
    }
  },
  digitalInvoiceContainer: {
    alignItems: 'center',
    fontSize: 8
  },
  digitalInvoiceText: {
    minWidth: 100,
    fontSize: 12,
    fontWeight: 500,
    textAlign: 'center',
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(0.5),
    borderRadius: theme.custom.borderRadius
  },
  physicalInvoiceText: {
    backgroundColor: theme.palette.color.gasesGreen
  }
}));

export default ContractItemGDC;
